import React, { useState, useEffect, memo, useContext } from 'react';
import { Button, Typography, Drawer } from 'antd';
import NodesConstructor from './ui/NodesConstructor';
import { useNodesState, useEdgesState, ReactFlowProvider } from '@xyflow/react';
import {
  useGetSceneTimelineByIdQuery,
  useCreateOneEventTimelineMutation,
  useDeleteOneEventTimelineMutation,
  useUpdateOneEventTimelineMutation,
  GetSceneTimelineByIdDocument,
} from '@src/components/generated/graphql';
import { updateTimelineInput } from './queries-input';
import type { TimelineType } from '@src/types/event-timeline';
import { TimelineSvg } from './svgs';
import styles from './timeline-node-manager.module.css';
import { ThemeContext } from '../../../../pages/_app';
import { openNotification } from 'util/helpers';

const { Title, Text } = Typography;

interface EventTimelineProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  saveTimelineRef: React.RefObject<VoidFunction | null>;
  selectableAssets?: any[];
  userSceneId: string;
  isFullscreen?: boolean;
  onSetNodesReady?: (setNodes: any) => void;
}

const TimelineNodeManager: React.FC<EventTimelineProps> = ({
  isOpen,
  setIsOpen,
  selectableAssets = [],
  userSceneId,
  isFullscreen,
  onSetNodesReady,
  saveTimelineRef,
}) => {
  const { isDark } = useContext(ThemeContext);
  const [timeline, setTimeline] = useState<TimelineType | undefined | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpenInternal, setIsOpenInternal] = useState<boolean>(false);
  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [createTimelineM] = useCreateOneEventTimelineMutation();
  const [deleteTimeline] = useDeleteOneEventTimelineMutation();
  const [updateTimelineM] = useUpdateOneEventTimelineMutation();

  const {
    data: timeLineQ,
    refetch: refetchTimeline,
    loading: queryLoading,
  } = useGetSceneTimelineByIdQuery({
    variables: {
      where: {
        sceneId: userSceneId,
      },
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const timelineData = timeLineQ?.getSceneTimelineById?.timeline;

  useEffect(() => {
    if (timeline) {
      const normalizeNodes = (nodes: any[]) => {
        return nodes.map((node) => {
          const {
            position,
            selected,
            zIndex,
            dragging,
            draggingSourceHandle,
            ...nodeWithoutUIProps
          } = node;
          return nodeWithoutUIProps;
        });
      };

      const currentNodes = JSON.stringify(normalizeNodes(nodes));
      const currentEdges = JSON.stringify(edges);
      const savedNodes = JSON.stringify(
        normalizeNodes(timeline.nodesConfig?.nodes || [])
      );
      const savedEdges = JSON.stringify(timeline.nodesConfig?.edges || []);

      const hasNodesChanged = currentNodes !== savedNodes;
      const hasEdgesChanged = currentEdges !== savedEdges;

      setHasChanges(hasNodesChanged || hasEdgesChanged);
    } else {
      setHasChanges(false);
    }
  }, [nodes, edges, timeline]);

  async function updateTimeline() {
    try {
      const input = updateTimelineInput(nodes, edges, timelineData!);
      const result = await updateTimelineM(input);

      if (result.data?.updateOneEventTimeline) {
        openNotification('Success', 'Timeline updated successfully!');
        setError(null);
      } else if (result.errors) {
        throw new Error(result.errors.map((e) => e.message).join(', '));
      }
    } catch (err: any) {
      console.error('Error updating timeline:', err);
      setError(`Timeline save failed: ${err.message || err}`);
      openNotification('Error', `Timeline save failed: ${err.message || err}`);
    }
  }

  async function createTimeline() {
    try {
      const result = await createTimelineM({
        variables: {
          data: {
            name: 'New Timeline',
            userSceneId: userSceneId,
            nodesConfig: {
              create: {
                edges: [],
                nodes: [],
              },
            },
          },
        },
        refetchQueries: [GetSceneTimelineByIdDocument],
        awaitRefetchQueries: true,
      });

      if (result.data?.createOneEventTimeline) {
        openNotification('Success', 'Timeline created successfully!');
      } else if (result.errors) {
        throw new Error(result.errors.map((e) => e.message).join(', '));
      }
    } catch (err) {
      console.error('Error creating timeline:', err);
      openNotification('Error', `Timeline creation failed: ${err}`);
    }
  }

  const saveTimeline = async () => {
    setLoading(true);
    setError(null);

    try {
      if (!userSceneId) {
        return;
      }

      if (!timeline || !timeline.id) {
        await createTimeline();
        return;
      }

      await updateTimeline();
    } catch (err: any) {
      console.error('Error saving timeline:', err);

      if (err.graphQLErrors) {
        console.error('GraphQL errors:', err.graphQLErrors);
        setError(
          `Failed to save timeline: ${
            err.graphQLErrors[0]?.message || 'Unknown GraphQL error'
          }`
        );
      } else {
        setError(
          `Failed to save timeline: ${err.message || 'Please try again.'}`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTimeline = async () => {
    setLoading(true);
    setError(null);
    try {
      setNodes([]);
      setEdges([]);
      setTimeline(null);

      if (timeline?.id) {
        await deleteTimeline({
          variables: {
            where: {
              id: timeline.id,
            },
          },
          refetchQueries: [GetSceneTimelineByIdDocument],
          awaitRefetchQueries: true,
        });
      }

      await createTimeline();
    } catch (error) {
      console.error('Error clearing timeline:', error);
      setError('Failed to clear timeline. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTimeline = () => {
    saveTimeline();
  };

  useEffect(() => {
    if (queryLoading) return;

    if (!timelineData) {
      setTimeline(null);
      setNodes([]);
      setEdges([]);
      return;
    }

    if (timelineData.nodesConfig?.edges) {
      setEdges(timelineData.nodesConfig?.edges);
    }

    if (timelineData.nodesConfig?.nodes) {
      const nodesWithZIndex = timelineData.nodesConfig?.nodes.map(
        (n, index) => {
          const node = JSON.parse(JSON.stringify(n));
          if (node.zIndex === undefined || node.zIndex === null) {
            node.zIndex = index;
          }
          return node;
        }
      );
      setNodes(nodesWithZIndex);
    }

    setTimeline(timelineData);

    if (timelineData?.events) {
      (window as any).timelineData = timelineData;
    }
  }, [timelineData, queryLoading]);

  useEffect(() => {
    if (onSetNodesReady) {
      onSetNodesReady(setNodes);
    }
  }, [setNodes, onSetNodesReady]);

  useEffect(() => {
    if (!saveTimelineRef) {
      return;
    }

    saveTimelineRef.current = saveTimeline;
  }, [saveTimelineRef, saveTimeline]);

  const timelineContent = (
    <div>
      {error && (
        <div className={styles.errorBlock}>
          <Text className={styles.errorText}>Error: {error}</Text>
        </div>
      )}
      {!timeline ? (
        <div
          className={`${styles.noTimelineBlock} ${isDark ? styles.dark : ''}`}
          style={{
            backgroundColor: isDark ? '#1E1F1F' : '#FFF',
            borderColor: isDark ? 'rgba(38, 38, 38, 1)' : '#a3a3a3',
          }}
        >
          {TimelineSvg}
          <Title level={4} className={styles.noTimelineTitle}>
            No Timeline Yet
          </Title>
          <Text className={styles.noTimelineText}>
            No timeline exists for this scene yet.
          </Text>
          <Button
            type='primary'
            onClick={handleSaveTimeline}
            size='large'
            className={styles.noTimelineButton}
            loading={loading}
          >
            Create Timeline
          </Button>
        </div>
      ) : (
        <ReactFlowProvider>
          <NodesConstructor
            nodes={Array.isArray(nodes) ? nodes : []}
            edges={Array.isArray(edges) ? edges : []}
            setEdges={setEdges}
            setNodes={setNodes}
            selectableAssets={selectableAssets}
            saveTimeline={saveTimeline}
            deleteTimeline={handleDeleteTimeline}
            hasChanges={hasChanges}
            loading={loading}
          />
          {loading && (
            <div className={styles.loadingOverlay}>
              <div className={styles.loadingBlock}>
                <Text>Loading timeline...</Text>
              </div>
            </div>
          )}
        </ReactFlowProvider>
      )}
    </div>
  );

  const NewTemplateSceneContent = (
    <div
      className={`${styles.noTimelineBlock} ${isDark ? styles.dark : ''}`}
      style={{
        backgroundColor: isDark ? '#1E1F1F' : '#FFF',
        borderColor: isDark ? 'rgba(38, 38, 38, 1)' : '#a3a3a3',
      }}
    >
      {TimelineSvg}
      <Title level={4} className={styles.noTimelineTitle}>
        Template is not saved
      </Title>
      <Text className={styles.noTimelineText}>
        Please save the scene to be able configure Timeline.
      </Text>
    </div>
  );

  return (
    <>
      {isFullscreen ? (
        <div style={{ height: '100%', width: '100%' }}>
          {!userSceneId ? NewTemplateSceneContent : timelineContent}
        </div>
      ) : (
        <Drawer
          open={isOpen || isOpenInternal}
          onClose={() => {
            setIsOpen(false);
            setIsOpenInternal(false);
          }}
          closeIcon={false}
          footer={false}
          width={'90%'}
          zIndex={1001}
        >
          {!userSceneId ? NewTemplateSceneContent : timelineContent}
        </Drawer>
      )}
    </>
  );
};

export default memo(TimelineNodeManager);
